import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Avatar, Box, Button, LinearProgress, Paper, Typography, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { UserAvatar } from '@/components/common';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  TimelineCalendar,
  TimelineItem,
  TimelineRow,
  TimelineRowGroup,
  TimelinePinpoint,
  TimelinePinpointGroup,
  DayCalendar,
  type Mechanic as DayCalendarMechanic,
  type ScheduleEvent,
  type DayCalendarEventDrop,
} from 'mq-timeline-calendar/react';
import { calendarLocaleFor } from '@/features/calendar/supervisorTaskCalendar';
import { currentLanguage } from '@/utils/locale';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addWeeks,
  addMonths,
  subDays,
  subWeeks,
  subMonths,
  setHours,
  format,
  isSameDay,
} from 'date-fns';
import { useUIStore } from '@/stores/uiStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { useSwipe } from '@/hooks/useSwipe';
import { useHandOffWorkOrder } from '@/hooks/useWorkOrderWindow';
import { ServiceCalendarItem } from './ServiceCalendarItem';
import { CalendarClickOverlay } from './ClickableCalendarRow';
import { AbsenceCalendarItem } from './AbsenceCalendarItem';
import { DateOnlyDayItem } from './DateOnlyDayItem';
import { UnplacedWorkBucket } from './UnplacedWorkBucket';
import { UtilizationSummary } from './UtilizationSummary';
import {
  buildDailyUtilization,
  describeCalendarCapacityGap,
  UTILIZATION_ROW_ID,
  UTILIZATION_ROW_HEIGHT,
  type CalendarCapacityGap,
} from '@/features/calendar/calendarUtilization';
import { isMergedItem, mergeWorkOrderItems } from '@/features/calendar/calendarMerge';
import { CalendarStatusLegend } from './CalendarStatusLegend';
import { buildAbsenceBlocks } from '@/features/calendar/calendarAbsences';
import { groupDateOnlyItems, type DateOnlyDayGroup } from '@/features/calendar/calendarDateOnly';
import type { UnplacedCalendarWork } from '@/features/calendar/calendarSegments';
import {
  dayColumnMinWidth,
  resolveCalendarDensity,
  resolveSelectedMechanicIds,
  shouldDefaultToTimeline,
  timelineEventHeight,
  timelineRowHeight,
  TIMELINE_DENSITY,
  useCalendarMechanicPrefsStore,
  type CalendarDensity,
} from '@/features/calendar/calendarMechanicPrefs';
import type { CalendarAbsence } from '@/hooks/useBookings';
import { CalendarToolbar, type CalendarView } from './CalendarToolbar';
import { MechanicFilter } from './MechanicFilter';
import { mechanicIdsAbsentForRange } from './absentMechanics';
import { reassignNonMechanicItems } from './nonMechanicAssignee';
import { QuoteAcceptFlow, WorkOrderCreateModal, WorkOrderDetailDialog } from '@/components/workorders';
import { useNotify } from '@/stores/notificationStore';
import { EmptySlotChoiceDialog, OpenQuotePickerDialog } from './EmptySlotDialogs';
import {
  getBusinessHoursRange,
  getClosedRangesWithin,
  getEarliestBusinessStart,
  getOpeningRangesForDate,
  hasWeekendHours,
  stepToOpenDay,
  buildHoursExceptionIndex,
  applyDateOverrideToWeekly,
  applyRangeOverridesToWeekly,
  buildOpenRangeTimestamps,
  ALL_WEEKDAYS_CLOSED,
} from '@/lib/inventoryUtils';
import { useTenantSettings, toAvailabilityWeekly, type CalendarHours } from '@/hooks/useTenantSettings';
import { useHoursExceptions } from '@/hooks/useHoursExceptions';
import { calendarColors } from '@/styles/theme';
import type { ComputedShift } from '@/hooks/useShifts';
import type { Mechanic, CalendarEvent, BookingStatus, ClockTime, CalendarServiceItem } from '@/types';

interface WorkshopCalendarProps {
  serviceItems: CalendarServiceItem[];
  /** Floating work with no mechanic placement — listed above the grid (#2686). */
  unplacedWork?: UnplacedCalendarWork[];
  mechanics: Mechanic[];
  events?: CalendarEvent[];
  shifts?: ComputedShift[];
  clockTimes?: ClockTime[];
  /** Sick leaves and vacations — drawn as their own bar on the mechanic's row. */
  absences?: CalendarAbsence[];
  utilizationReady?: boolean;
  /**
   * Bookings for the visible range are still being fetched — first load or a
   * navigation to a range not yet in cache. Not background polling (#2745).
   */
  loading?: boolean;
  onReschedule?: (serviceItemId: string, bookingId: string, newStart: Date) => void;
  onAssign?: (serviceItemId: string, bookingId: string, mechanicId: string | null, newStart: Date) => void;
  onVisibleRangeChange?: (start: Date, end: Date) => void;
  /**
   * Fill the parent's available height instead of using a fixed `calc(100vh - …)`
   * height. Requires the parent chain to be a definite-height flex column (see
   * AppShell full-bleed layout). Ignored on mobile, which keeps the fixed height.
   */
  fillHeight?: boolean;
}

// Maps each event type to a theme palette path; resolved to concrete colors
// inside the component (these feed the timeline lib's `color` prop, which is a
// raw string — not an sx token).
const eventColorPaths: Record<CalendarEvent['type'], (t: Theme) => string> = {
  inspection_completed: (t) => t.palette.success.dark,
  approval_received: (t) => t.palette.secondary.dark,
  customer_arrived: (t) => t.palette.primary.main,
  vehicle_ready: (t) => t.palette.success.main,
};

const eventIcons: Record<CalendarEvent['type'], string> = {
  inspection_completed: '✓',
  approval_received: '👍',
  customer_arrived: '👤',
  vehicle_ready: '🚗',
};

// Compact single-line work-order bars (#1337): only registration on the chip;
// remaining details live in the tooltip. Concurrent jobs stack in EVENT_SLOT
// slices; every bar uses fixed EVENT_HEIGHT so a lone item never stretches.
// Comfortable MIN_ROW_HEIGHT is taller than one slot so the sidebar (avatar +
// first/last name on two caption lines) fits when the row has no overlaps.
// Compact density (#2657) uses ~36px rows / 24px bars for 15-mechanic shops.
// mq-timeline-calendar cloneElement forces marginTop/marginBottom to 4px on
// TimelineItem; account for that when vertically centering on tall single-slot rows.
const LIBRARY_ITEM_MARGIN_Y = 4;

/** Fixed bar size; on single-slot (min-height) rows, center the bar vertically. */
function eventItemStyle(rowHeight: number, density: CalendarDensity): {
  height: number;
  boxSizing: 'border-box';
  transform?: string;
} {
  const metrics = TIMELINE_DENSITY[density];
  const eventHeight = timelineEventHeight(density);
  // Multi-overlap rows: library places items into EVENT_SLOT_HEIGHT slices;
  // the forced 4px top margin already centers the 24px bar in a 32px slice.
  if (rowHeight >= metrics.eventSlotHeight * 2) {
    return { height: eventHeight, boxSizing: 'border-box' };
  }
  // Single-slot row (often minRowHeight): center in the full row.
  const targetTop = Math.round((rowHeight - eventHeight) / 2);
  const extra = Math.max(0, targetTop - LIBRARY_ITEM_MARGIN_Y);
  return {
    height: eventHeight,
    boxSizing: 'border-box',
    ...(extra > 0 ? { transform: `translateY(${extra}px)` } : {}),
  };
}

// Full 00–24 opening hours for every weekday (ISO keys, like every other
// CalendarHours in the app). The vertical DayCalendar derives its visible time
// axis solely from `availability`, so this is what makes the "show the whole
// day" toggle widen the axis there.
const FULL_DAY_RANGE = [{ start: '00:00', end: '24:00' }];
const FULL_DAY_HOURS: CalendarHours = {
  1: FULL_DAY_RANGE,
  2: FULL_DAY_RANGE,
  3: FULL_DAY_RANGE,
  4: FULL_DAY_RANGE,
  5: FULL_DAY_RANGE,
  6: FULL_DAY_RANGE,
  7: FULL_DAY_RANGE,
};
const FULL_DAY_AVAILABILITY = toAvailabilityWeekly(FULL_DAY_HOURS);

// Vertical day view grid geometry. Passed to DayCalendar explicitly so the
// closed-hours shading below can convert minutes to pixels — the shading is
// painted as a gradient on the mechanic columns, since DayCalendar styles all
// its slots alike and cannot tint individual ones (#1255).
const VERTICAL_SLOT_MINUTES = 30;
const VERTICAL_SLOT_HEIGHT = 40;
// Drops snap finer than the 30-min grid so a job can land on a quarter hour.
const VERTICAL_DRAG_SNAP_MINUTES = 15;
const MINUTES_PER_DAY = 24 * 60;

/** Anything the row packs and scales by: service items and absence blocks. */
interface TimeSpan {
  scheduledStart: string;
  scheduledEnd: string;
}

/** `ScheduleEvent.data` payload marking a vertical-view event as an absence. */
interface AbsenceEventData {
  absenceLabelKey: string;
}

function isAbsenceEventData(data: unknown): data is AbsenceEventData {
  return typeof data === 'object' && data !== null && 'absenceLabelKey' in data;
}

/** `ScheduleEvent.data` payload for a mechanic's date-only day list (#2760). */
interface DateOnlyEventData {
  dateOnlyGroup: DateOnlyDayGroup;
}

function isDateOnlyEventData(data: unknown): data is DateOnlyEventData {
  return typeof data === 'object' && data !== null && 'dateOnlyGroup' in data;
}

/**
 * Whether a service item can be moved on the calendar. Tentative items and
 * shift-spanning continuations follow their parent, and closed work stays put.
 */
function isServiceItemDraggable(item: CalendarServiceItem): boolean {
  return (
    !item.isTentative &&
    !item.isContinuationSegment &&
    !isMergedItem(item) &&
    item.bookingStatus !== 'completed' &&
    item.bookingStatus !== 'cancelled' &&
    item.bookingStatus !== 'quote_rejected'
  );
}

/** A vertical-view event carrying a movable service item (not an absence or day list). */
function isDraggableDayEvent(event: ScheduleEvent): boolean {
  if (isAbsenceEventData(event.data) || isDateOnlyEventData(event.data)) return false;
  const item = event.data as CalendarServiceItem | undefined;
  return item !== undefined && isServiceItemDraggable(item);
}

function CapacityGapAlert({
  gap,
  onShowAllMechanics,
}: {
  gap: CalendarCapacityGap;
  onShowAllMechanics: () => void;
}) {
  const { t } = useTranslation('calendar');
  if (gap === 'no_mechanics') {
    return (
      <Alert severity="info" sx={{ flexShrink: 0, mb: 1 }}>
        {t('capacityGap.noMechanics')}
      </Alert>
    );
  }
  if (gap === 'empty_filter') {
    return (
      <Alert
        severity="info"
        sx={{ flexShrink: 0, mb: 1 }}
        action={
          <Button color="inherit" size="small" onClick={onShowAllMechanics}>
            {t('capacityGap.emptyFilterAction')}
          </Button>
        }
      >
        {t('capacityGap.emptyFilter')}
      </Alert>
    );
  }
  return (
    <Alert
      severity="info"
      sx={{ flexShrink: 0, mb: 1 }}
      action={
        <Button
          color="inherit"
          size="small"
          component={RouterLink}
          to="/hr?tab=shift-patterns"
        >
          {t('capacityGap.noShiftsAction')}
        </Button>
      }
    >
      {t('capacityGap.noShifts')}
    </Alert>
  );
}

// Max concurrent items via a sweep-line. The timeline library divides each row's
// height by subRowCount within an overlap cluster, so this drives row scaling.
function maxOverlap(items: TimeSpan[], rangeStart: number, rangeEnd: number): number {
  const events: Array<[number, number]> = [];
  for (const it of items) {
    const s = Date.parse(it.scheduledStart);
    const e = Date.parse(it.scheduledEnd);
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
    if (e <= rangeStart || s >= rangeEnd) continue;
    events.push([s, 1]);
    events.push([e, -1]);
  }
  if (events.length === 0) return 0;
  // End events (-1) come before start events (+1) at the same instant
  // so adjacent (non-overlapping) intervals are not counted as overlapping.
  events.sort((a, b) => a[0] !== b[0] ? a[0] - b[0] : a[1] - b[1]);
  let cur = 0;
  let max = 0;
  for (const [, delta] of events) {
    cur += delta;
    if (cur > max) max = cur;
  }
  return max;
}

/** True when the item's time range intersects [rangeStart, rangeEnd). */
function itemIntersectsRange(
  item: TimeSpan,
  rangeStart: number,
  rangeEnd: number,
): boolean {
  const s = Date.parse(item.scheduledStart);
  const e = Date.parse(item.scheduledEnd);
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return false;
  return e > rangeStart && s < rangeEnd;
}

export function WorkshopCalendar({
  serviceItems: incomingServiceItems,
  unplacedWork = [],
  mechanics,
  events = [],
  shifts = [],
  clockTimes = [],
  absences = [],
  utilizationReady = false,
  loading = false,
  onReschedule,
  onAssign,
  onVisibleRangeChange,
  fillHeight = false,
}: WorkshopCalendarProps) {
  const { t } = useTranslation('calendar');
  const { t: tc } = useTranslation('common');
  const theme = useTheme();
  const themeMode = useUIStore((s) => s.themeMode);
  const isMobile = useIsMobile();
  const { calendarHours, workdayUnpaidLunchMinutes } = useTenantSettings();

  // Fill-height (flex) layout only on desktop; mobile keeps the fixed height so
  // the bottom nav / swipe interactions are unaffected.
  const fill = fillHeight && !isMobile;
  const calendarHeight = fill
    ? '100%'
    : isMobile
      ? 'calc(100vh - 290px)'
      : 'calc(100vh - 370px)';

  const calendarDateStr = useCalendarStore((s) => s.calendarDate);
  const view = useCalendarStore((s) => s.calendarView);
  const dayLayout = useCalendarStore((s) => s.dayLayout);
  const showExtendedHours = useCalendarStore((s) => s.showExtendedHours);
  const setCalendarDate = useCalendarStore((s) => s.setCalendarDate);
  const setCalendarView = useCalendarStore((s) => s.setCalendarView);
  const setDayLayout = useCalendarStore((s) => s.setDayLayout);
  const setShowExtendedHours = useCalendarStore((s) => s.setShowExtendedHours);
  const compressClosedHours = useCalendarStore((s) => s.compressClosedHours);
  const setCompressClosedHours = useCalendarStore((s) => s.setCompressClosedHours);

  const currentDate = useMemo(() => startOfDay(new Date(calendarDateStr)), [calendarDateStr]);
  const setCurrentDate = useCallback((dateOrFn: Date | ((prev: Date) => Date)) => {
    if (typeof dateOrFn === 'function') {
      setCalendarDate(dateOrFn(startOfDay(new Date(calendarDateStr))));
    } else {
      setCalendarDate(dateOrFn);
    }
  }, [calendarDateStr, setCalendarDate]);

  const handleViewChange = useCallback((v: CalendarView) => {
    setCalendarView(v);
  }, [setCalendarView]);

  // Switch to day view when entering mobile
  useEffect(() => {
    if (isMobile && view === 'week') {
      setCalendarView('day');
    }
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps
  const persistedMechanicIds = useCalendarMechanicPrefsStore((s) => s.selectedMechanicIds);
  const setPersistedMechanicIds = useCalendarMechanicPrefsStore((s) => s.setSelectedMechanicIds);
  const storedDensity = useCalendarMechanicPrefsStore((s) => s.density);
  const setStoredDensity = useCalendarMechanicPrefsStore((s) => s.setDensity);
  const dayLayoutTouched = useCalendarMechanicPrefsStore((s) => s.dayLayoutTouched);
  const markDayLayoutTouched = useCalendarMechanicPrefsStore((s) => s.markDayLayoutTouched);
  const mechanicGroups = useCalendarMechanicPrefsStore((s) => s.groups);
  const saveMechanicGroup = useCalendarMechanicPrefsStore((s) => s.saveGroup);
  const deleteMechanicGroup = useCalendarMechanicPrefsStore((s) => s.deleteGroup);
  const applyMechanicGroup = useCalendarMechanicPrefsStore((s) => s.applyGroup);
  const hideAbsentMechanics = useCalendarMechanicPrefsStore((s) => s.hideAbsentMechanics);
  const setHideAbsentMechanics = useCalendarMechanicPrefsStore((s) => s.setHideAbsentMechanics);
  const [selectedStatuses, setSelectedStatuses] = useState<BookingStatus[]>([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);

  // Default the mechanic filter to all available mechanics until the user
  // changes it. The last explicit selection is persisted so a 15-mechanic shop
  // does not have to re-filter after every visit (#2657). Stale ids are dropped.
  const selectedMechanicIds = useMemo(
    () => resolveSelectedMechanicIds(persistedMechanicIds, mechanics).ids,
    [persistedMechanicIds, mechanics],
  );
  const setSelectedMechanicIds = setPersistedMechanicIds;
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createInitialDate, setCreateInitialDate] = useState<Date | undefined>();
  const [createInitialMechanicId, setCreateInitialMechanicId] = useState<string | undefined>();
  // An empty cell asks what it becomes: a new work order or an open quote
  // (#2653). The slot is kept until the choice resolves.
  const [emptySlot, setEmptySlot] = useState<{ date: Date; mechanicId: string | null } | null>(null);
  const [quotePickerOpen, setQuotePickerOpen] = useState(false);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState<string | null>(null);
  const notify = useNotify();

  // Per-date opening-hours overrides — holidays, shortened days (#1282). Fetched
  // unbounded rather than per visible range: the set is a few dozen rows a year,
  // and day-view navigation has to see past the window to skip a shutdown.
  const { exceptions: hoursExceptions } = useHoursExceptions();
  const exceptionIndex = useMemo(
    () => buildHoursExceptionIndex(hoursExceptions),
    [hoursExceptions],
  );

  // Business hours range for day view scaling
  const businessRange = useMemo(() => getBusinessHoursRange(calendarHours), [calendarHours]);
  const showWeekend = useMemo(() => hasWeekendHours(calendarHours), [calendarHours]);

  // Extended hours range shows the full day (0-24)
  const extendedRange = useMemo(() => ({ startHour: 0, endHour: 24 }), []);

  // Choose range based on showExtendedHours setting
  const activeRange = useMemo(() =>
    showExtendedHours ? extendedRange : businessRange,
    [showExtendedHours, extendedRange, businessRange]
  );

  // The day view shows ONE date, so its exception folds into the weekday key the
  // library reads. A week window works the same way — each weekday appears
  // exactly once in it (#1302). Only the month view repeats a weekday, and there
  // applyRangeOverridesToWeekly returns the plain pattern.
  const dayAvailabilityWeekly = useMemo(
    () => toAvailabilityWeekly(applyDateOverrideToWeekly(currentDate, calendarHours, exceptionIndex)),
    [currentDate, calendarHours, exceptionIndex]
  );

  // The horizontal timeline honours showExtendedHours via startDate/endDate
  // below, but the vertical DayCalendar clamps its axis to `availability`
  // alone — so the toggle has to reach it through the hours it is given.
  const dayCalendarAvailability = useMemo(
    () => ({ weekly: showExtendedHours ? FULL_DAY_AVAILABILITY : dayAvailabilityWeekly }),
    [showExtendedHours, dayAvailabilityWeekly]
  );

  // Calculate date range based on view
  // On mobile, use the raw currentDate so swiping shifts the window fluidly
  // On desktop (and for "Today" button), snap to clean boundaries
  const { startDate, endDate } = useMemo(() => {
    if (isMobile && view === 'day') {
      return {
        startDate: setHours(startOfDay(currentDate), activeRange.startHour),
        endDate: setHours(startOfDay(currentDate), activeRange.endHour),
      };
    }
    if (isMobile && view === 'week') {
      return {
        startDate: subDays(currentDate, 3),
        endDate: addDays(currentDate, 3),
      };
    }
    switch (view) {
      case 'day':
        return {
          startDate: setHours(startOfDay(currentDate), activeRange.startHour),
          endDate: setHours(startOfDay(currentDate), activeRange.endHour),
        };
      case 'week': {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        return {
          startDate: weekStart,
          endDate: showWeekend
            ? endOfWeek(currentDate, { weekStartsOn: 1 })
            : endOfDay(addDays(weekStart, 4)),
        };
      }
      case 'month':
        return {
          startDate: startOfMonth(currentDate),
          endDate: endOfMonth(currentDate),
        };
    }
  }, [currentDate, view, isMobile, activeRange, showWeekend]);

  // Availability for the horizontal timeline, resolved over the WINDOW that is
  // actually visible. Day and week windows name each weekday at most once, so
  // the exceptions in them are exact; a month window falls back to the plain
  // weekly pattern (#1302).
  const rangeAvailabilityWeekly = useMemo(
    () => toAvailabilityWeekly(
      applyRangeOverridesToWeekly(startDate, endDate, calendarHours, exceptionIndex),
    ),
    [startDate, endDate, calendarHours, exceptionIndex]
  );

  // A window longer than a week (the month view) cannot state a single closed
  // date through a weekday-keyed pattern, so it states its opening hours as
  // absolute spans instead: `specific` wins in the library's `isAvailable`, and
  // the all-closed weekly baseline underneath makes everything else unavailable
  // (#1302). Empty when there is nothing to override, or the window is absurd.
  const openSpans = useMemo(() => {
    if (exceptionIndex.size === 0) return [];
    const windowDays = (endDate.getTime() - startDate.getTime()) / 86_400_000;
    if (windowDays <= 7) return [];
    return buildOpenRangeTimestamps(startDate, endDate, calendarHours, exceptionIndex);
  }, [startDate, endDate, calendarHours, exceptionIndex]);

  const timelineAvailability = useMemo(
    () =>
      openSpans.length > 0
        ? { specific: openSpans, weekly: ALL_WEEKDAYS_CLOSED }
        : { weekly: rangeAvailabilityWeekly },
    [openSpans, rangeAvailabilityWeekly],
  );

  // Notify parent when visible date range changes so it can fetch matching data
  useEffect(() => {
    onVisibleRangeChange?.(startDate, endDate);
  }, [startDate, endDate, onVisibleRangeChange]);

  // Build clock time lookup: "userId|date" → ClockTime
  const clockTimeMap = useMemo(() => {
    const map = new Map<string, ClockTime>();
    for (const ct of clockTimes) {
      map.set(`${ct.employeeId}|${ct.date}`, ct);
    }
    return map;
  }, [clockTimes]);

  // Full useMechanics roster, not the selected subset. The parent defaults
  // mechanics to [] while that query is pending, which is indistinguishable
  // from an empty shop until `loading` clears (#3080).
  const knownMechanicIds = useMemo((): ReadonlySet<string> | null => {
    if (loading && mechanics.length === 0) return null;
    return new Set(mechanics.map((m) => m.id));
  }, [loading, mechanics]);

  const serviceItems = useMemo(
    () => reassignNonMechanicItems(incomingServiceItems, knownMechanicIds),
    [incomingServiceItems, knownMechanicIds],
  );

  // Extract unique service types from items
  const serviceTypes = useMemo(() => {
    const types = new Set<string>();
    serviceItems.forEach((item) => types.add(item.serviceName));
    return Array.from(types).sort();
  }, [serviceItems]);

  // Filter mechanics. Empty selection means "no filter" only after the user has
  // persisted an explicit selection; until then it is the all-available default
  // (#2333). A stored empty array is an empty filter (#2657).
  const selectedMechanicsList = useMemo(() => {
    if (selectedMechanicIds.length === 0) {
      if (persistedMechanicIds === null) {
        return mechanics.filter((m) => m.isAvailable);
      }
      return [];
    }
    return mechanics.filter((m) => selectedMechanicIds.includes(m.id));
  }, [mechanics, selectedMechanicIds, persistedMechanicIds]);

  const fullyAbsentIds = useMemo(
    () =>
      new Set(
        mechanicIdsAbsentForRange({
          start: startDate,
          end: endDate,
          absences,
          isOpenDay: (date) => {
            const isoWeekday = date.getDay() === 0 ? 7 : date.getDay();
            return (calendarHours[isoWeekday]?.length ?? 0) > 0;
          },
        }),
      ),
    [startDate, endDate, absences, calendarHours],
  );

  // Hide mechanics who are away for the whole visible range. They remain in
  // the picker (with a "poissa" hint) so a foreman can still select their row.
  const filteredMechanics = useMemo(() => {
    if (!hideAbsentMechanics) return selectedMechanicsList;
    return selectedMechanicsList.filter((m) => !fullyAbsentIds.has(m.id));
  }, [selectedMechanicsList, hideAbsentMechanics, fullyAbsentIds]);

  const pickerMechanics = useMemo(() => {
    const hint = t('hideAbsent.hint');
    return mechanics.map((m) =>
      fullyAbsentIds.has(m.id) ? { ...m, lastName: `${m.lastName} (${hint})` } : m,
    );
  }, [mechanics, fullyAbsentIds, t]);

  const utilizationMechanicIds = useMemo(
    () => filteredMechanics.map((m) => m.id),
    [filteredMechanics],
  );

  const availableMechanicCount = useMemo(
    () => mechanics.filter((m) => m.isAvailable).length,
    [mechanics],
  );

  const dailyUtilization = useMemo(() => buildDailyUtilization({
    start: startDate, end: endDate,
    mechanicIds: utilizationMechanicIds,
    shifts, items: serviceItems,
    unpaidLunchMinutes: workdayUnpaidLunchMinutes,
  }), [startDate, endDate, utilizationMechanicIds, shifts, serviceItems, workdayUnpaidLunchMinutes]);

  const capacityGap = useMemo(() => {
    if (!utilizationReady) return null;
    // When hide-absent emptied the columns, keep the user's selection so the
    // calendar does not look like a cleared mechanic filter (#2902).
    const idsForGap = utilizationMechanicIds.length > 0
      ? utilizationMechanicIds
      : selectedMechanicIds;
    return describeCalendarCapacityGap(dailyUtilization, {
      selectedMechanicIds: idsForGap,
      availableMechanicCount,
    });
  }, [utilizationReady, dailyUtilization, utilizationMechanicIds, selectedMechanicIds, availableMechanicCount]);

  const handleShowAllMechanics = useCallback(() => {
    setSelectedMechanicIds(mechanics.filter((m) => m.isAvailable).map((m) => m.id));
  }, [mechanics, setSelectedMechanicIds]);

  const density = resolveCalendarDensity(storedDensity, filteredMechanics.length);
  const densityMetrics = TIMELINE_DENSITY[density];

  useEffect(() => {
    if (!shouldDefaultToTimeline(filteredMechanics.length, dayLayoutTouched)) return;
    if (dayLayout !== 'horizontal') {
      setDayLayout('horizontal');
    }
  }, [filteredMechanics.length, dayLayoutTouched, dayLayout, setDayLayout]);

  const handleDayLayoutChange = useCallback((layout: typeof dayLayout) => {
    markDayLayoutTouched();
    setDayLayout(layout);
  }, [markDayLayoutTouched, setDayLayout]);

  const handleSaveMechanicGroup = useCallback((name: string) => {
    saveMechanicGroup(name, selectedMechanicIds);
  }, [saveMechanicGroup, selectedMechanicIds]);

  const handleApplyMechanicGroup = useCallback((id: string) => {
    const ids = applyMechanicGroup(id);
    if (!ids) return;
    const known = new Set(mechanics.map((m) => m.id));
    setSelectedMechanicIds(ids.filter((mechanicId) => known.has(mechanicId)));
  }, [applyMechanicGroup, mechanics, setSelectedMechanicIds]);

  // Sick leave and vacation days have no shift (#2261) — this bar takes the
  // shift bar's place so the planner sees why the row is empty.
  const absenceBlocks = useMemo(() => buildAbsenceBlocks({
    start: startDate, end: endDate,
    mechanicIds: filteredMechanics.map((m) => m.id), absences,
  }), [absences, filteredMechanics, startDate, endDate]);

  // Filter service items by mechanic, status, and service type.
  // Mechanic rows and work orders share the same selection: an empty set
  // hides assigned work rather than meaning "no mechanic filter" (#2333).
  const filteredItems = useMemo(() => {
    const visibleMechanicIds = new Set(utilizationMechanicIds);
    return serviceItems.filter((item) => {
      if (item.assignedMechanicId !== null && !visibleMechanicIds.has(item.assignedMechanicId)) {
        return false;
      }
      // Status filter — empty selection means no filter
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(item.bookingStatus)) {
        return false;
      }
      // Service type filter
      if (selectedServiceTypes.length > 0) {
        if (!selectedServiceTypes.includes(item.serviceName)) {
          return false;
        }
      }
      return true;
    });
  }, [serviceItems, utilizationMechanicIds, selectedStatuses, selectedServiceTypes]);

  // Date-only history is one whole-day list per mechanic and day, not a timed
  // block each (#2760). Everything below that draws or sizes timed bars uses
  // `timedItems`; the day lists come from `dateOnlyGroups`.
  const { timed: timedItems, groups: dateOnlyGroups } = useMemo(
    () => groupDateOnlyItems(filteredItems),
    [filteredItems],
  );

  // Items without a mechanic assigned
  const unassignedItems = useMemo(() => {
    return timedItems.filter((item) => !item.assignedMechanicId);
  }, [timedItems]);
  // One mechanic's back-to-back jobs on one work order draw as one entry.
  const mechanicItems = useMemo(
    () => mergeWorkOrderItems(timedItems.filter((item) => item.assignedMechanicId !== null)),
    [timedItems],
  );
  const unassignedDateOnlyGroups = useMemo(
    () => dateOnlyGroups.filter((group) => group.mechanicId === null),
    [dateOnlyGroups],
  );

  // Visible time window for row-height calculation. Heights must follow the
  // scrolled viewport, not the full startDate/endDate bounds — otherwise a
  // busy morning keeps every mechanic row tall while the user scrolls through
  // a quiet afternoon. Debounced so we don't remount all rows on every scroll
  // frame (rowHeightsSignature forces a coordinated remount when heights change).
  const [heightViewportMs, setHeightViewportMs] = useState<{
    start: number;
    end: number;
  }>(() => ({ start: startDate.getTime(), end: endDate.getTime() }));

  useEffect(() => {
    setHeightViewportMs({ start: startDate.getTime(), end: endDate.getTime() });
  }, [startDate, endDate]);

  const viewportHeightDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleViewportChange = useCallback((start: Date, end: Date) => {
    if (viewportHeightDebounceRef.current) {
      clearTimeout(viewportHeightDebounceRef.current);
    }
    viewportHeightDebounceRef.current = setTimeout(() => {
      const s = start.getTime();
      const e = end.getTime();
      if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return;
      // Small pad so items just entering the edge still influence height and
      // the row doesn't thrash as bars cross the clip boundary.
      const pad = Math.max(60_000, (e - s) * 0.05);
      setHeightViewportMs({ start: s - pad, end: e + pad });
    }, 140);
  }, []);

  useEffect(() => () => {
    if (viewportHeightDebounceRef.current) {
      clearTimeout(viewportHeightDebounceRef.current);
    }
  }, []);

  // Per-row height: scale by max concurrent items *in the visible viewport*
  // so each concurrent job keeps an EVENT_SLOT_HEIGHT slice, never below
  // minRowHeight (sidebar labels). Item bars use fixed EVENT_HEIGHT.
  const rowHeights = useMemo(() => {
    const rangeStart = heightViewportMs.start;
    const rangeEnd = heightViewportMs.end;
    const heights = new Map<string, number>();
    const unassignedOverlap = maxOverlap(
      [...unassignedItems, ...unassignedDateOnlyGroups],
      rangeStart,
      rangeEnd,
    );
    heights.set('unassigned', timelineRowHeight(unassignedOverlap, density));
    for (const m of filteredMechanics) {
      const items: TimeSpan[] = mechanicItems.filter((it) => it.assignedMechanicId === m.id);
      const overlap = maxOverlap(
        [
          ...items,
          ...dateOnlyGroups.filter((g) => g.mechanicId === m.id),
          ...absenceBlocks.filter((b) => b.mechanicId === m.id),
        ],
        rangeStart,
        rangeEnd,
      );
      heights.set(m.id, timelineRowHeight(overlap, density));
    }
    return heights;
  }, [
    mechanicItems, unassignedItems, dateOnlyGroups, unassignedDateOnlyGroups,
    filteredMechanics, absenceBlocks, heightViewportMs, density,
  ]);

  const getRowHeight = useCallback(
    (rowId: string) => rowHeights.get(rowId) ?? densityMetrics.minRowHeight,
    [rowHeights, densityMetrics.minRowHeight],
  );

  // The library assigns each TimelineRow a monotonically increasing order on
  // mount and never updates it. If a single row's height changes, only that
  // row re-registers (useEffect cleanup + register) and gets a higher order
  // than its siblings — it jumps to the bottom of the sort, breaking sidebar
  // alignment. Including this signature in every row's key forces all rows
  // to remount together when any height changes, so orders are reassigned in
  // JSX order.
  const rowHeightsSignature = useMemo(
    () => Array.from(rowHeights.entries())
      .map(([id, h]) => `${id}:${h}`)
      .sort()
      .join('|'),
    [rowHeights],
  );

  // Navigation handlers. In day view the step skips days the workshop is
  // closed (#1276) — the week view already collapses to Mon–Fri via
  // `showWeekend`, so this keeps browsing consistent between the two. "Today"
  // and the date picker deliberately do not skip: those are explicit choices,
  // and they stay the way to reach a closed day.
  const handlePrevious = useCallback(() => {
    switch (view) {
      case 'day':
        setCurrentDate((d) => stepToOpenDay(d, -1, calendarHours, exceptionIndex));
        break;
      case 'week':
        setCurrentDate((d) => subWeeks(d, 1));
        break;
      case 'month':
        setCurrentDate((d) => subMonths(d, 1));
        break;
    }
  }, [view, setCurrentDate, calendarHours, exceptionIndex]);

  const handleNext = useCallback(() => {
    switch (view) {
      case 'day':
        setCurrentDate((d) => stepToOpenDay(d, 1, calendarHours, exceptionIndex));
        break;
      case 'week':
        setCurrentDate((d) => addWeeks(d, 1));
        break;
      case 'month':
        setCurrentDate((d) => addMonths(d, 1));
        break;
    }
  }, [view, setCurrentDate, calendarHours, exceptionIndex]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, [setCurrentDate]);

  // Open a work order: in the second window when one is live (#3654),
  // otherwise in the dialog here.
  const handOffWorkOrder = useHandOffWorkOrder();
  const openWorkOrder = useCallback((bookingId: string) => {
    handOffWorkOrder(bookingId, () => {
      setSelectedWorkOrderId(bookingId);
      setModalOpen(true);
    });
  }, [handOffWorkOrder]);

  // Service item click — open the work order for the parent booking
  const handleItemClick = useCallback((item: CalendarServiceItem) => {
    openWorkOrder(item.bookingId);
  }, [openWorkOrder]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedWorkOrderId(null);
  }, []);

  // Actual business hours start (not padded for display)
  const businessStartTime = useMemo(() => getEarliestBusinessStart(calendarHours), [calendarHours]);

  // Find the snap time for a mechanic: shift start or business hours start
  const getAssignSnapTime = useCallback(
    (mechanicId: string | null, bookingDate: Date): Date => {
      const dateStr = format(bookingDate, 'yyyy-MM-dd');
      if (mechanicId) {
        // Find the mechanic's shift on this date
        const shift = shifts.find((s) => s.userId === mechanicId && s.date === dateStr);
        if (shift) {
          return new Date(`${dateStr}T${shift.startTime}`);
        }
      }
      // Fallback to actual business hours start
      return new Date(`${dateStr}T${businessStartTime}`);
    },
    [shifts, businessStartTime]
  );

  const handleDragEnd = useCallback(
    (
      serviceItemId: string,
      bookingId: string,
      newTime: number,
      _oldTime: number,
      newRowGroupId?: string,
      originalRowGroupId?: string,
    ) => {
      if (newRowGroupId === UTILIZATION_ROW_ID) return;
      const rowChanged = newRowGroupId !== undefined &&
        originalRowGroupId !== undefined &&
        newRowGroupId !== originalRowGroupId;

      if (rowChanged && onAssign) {
        const mechanicId = newRowGroupId === 'unassigned' ? null : newRowGroupId;
        // Snap time to mechanic's shift start or business hours start
        const snapTime = getAssignSnapTime(mechanicId, new Date(newTime));
        onAssign(serviceItemId, bookingId, mechanicId, snapTime);
      } else if (onReschedule) {
        // Outcome toasts (moved / pinned+undo / failed) come from the async
        // handler in WorkOrderCalendarTab, which knows how the move resolved.
        onReschedule(serviceItemId, bookingId, new Date(newTime));
      }
    },
    [onReschedule, onAssign, getAssignSnapTime]
  );

  const handleNewWorkOrder = useCallback(() => {
    setCreateInitialDate(undefined);
    setCreateInitialMechanicId(undefined);
    setCreateModalOpen(true);
  }, []);

  const handleEmptyCellClick = useCallback((clickedDate: Date, mechanicId: string | null) => {
    setEmptySlot({ date: clickedDate, mechanicId });
  }, []);

  const handleSlotNewWorkOrder = useCallback(() => {
    if (!emptySlot) return;
    setCreateInitialDate(emptySlot.date);
    setCreateInitialMechanicId(emptySlot.mechanicId ?? undefined);
    setEmptySlot(null);
    setCreateModalOpen(true);
  }, [emptySlot]);

  const handleSlotBookQuote = useCallback(() => {
    setQuotePickerOpen(true);
  }, []);

  const handleQuotePicked = useCallback((quoteId: string) => {
    setQuotePickerOpen(false);
    setAcceptingQuoteId(quoteId);
  }, []);

  const handleQuoteFlowClosed = useCallback(() => {
    setAcceptingQuoteId(null);
    setEmptySlot(null);
  }, []);

  const handleQuoteAccepted = useCallback((_workOrderId: string, workOrderNumber: string | null) => {
    notify.success(t('quoteBooked'), t('quoteBookedDesc', { number: workOrderNumber ?? '' }));
    setAcceptingQuoteId(null);
    setEmptySlot(null);
  }, [notify, t]);

  const emptySlotMechanicName = useMemo(() => {
    if (!emptySlot?.mechanicId) return null;
    const mechanic = mechanics.find((m) => m.id === emptySlot.mechanicId);
    return mechanic ? `${mechanic.firstName} ${mechanic.lastName}`.trim() : null;
  }, [emptySlot, mechanics]);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setCreateInitialDate(undefined);
    setCreateInitialMechanicId(undefined);
  }, []);

  // Discrete swipe: each swipe navigates one step (day/week/month)
  const handleSwipe = useCallback((deltaX: number) => {
    // Swipe left (negative deltaX) = forward in time
    if (deltaX < 0) {
      handleNext();
    } else {
      handlePrevious();
    }
  }, [handleNext, handlePrevious]);

  const swipeHandlers = useSwipe({ onSwipe: handleSwipe, threshold: 50 });

  // === DayCalendar (vertical day view) ===
  // Only computed when the vertical layout is active to avoid wasted work in
  // horizontal / week / month modes.
  const useVerticalDayView = view === 'day' && dayLayout === 'vertical';

  const UNASSIGNED_COLUMN_ID = 'unassigned';

  // #611: when the vertical day view is showing TODAY, auto-scroll so the
  // current-time line is centred in the viewport on mount / view-switch /
  // navigate-back-to-today. `mq-timeline-calendar`'s DayCalendar exposes the
  // current-time line via the `classNames.currentTimeLine` prop, so we tag
  // it with a marker class and look it up after layout. Other days keep
  // their natural scroll position (start of business hours).
  // Also re-centres when the axis grows to the full day (#1245) — otherwise
  // toggling it would park the viewport at 00:00.
  const dayCalendarContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!useVerticalDayView) return;
    if (!isSameDay(currentDate, new Date())) return;
    const root = dayCalendarContainerRef.current;
    if (!root) return;
    // Defer one frame so the DayCalendar has rendered the time line.
    const frame = requestAnimationFrame(() => {
      const timeLine = root.querySelector(
        '.workshop-calendar-current-time'
      ) as HTMLElement | null;
      if (timeLine) {
        timeLine.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [useVerticalDayView, currentDate, showExtendedHours]);

  // Closed hours, painted as a gradient behind each mechanic column so the
  // opening hours stand out the way they do in the horizontal timeline (#1255).
  // DayCalendar has no per-slot styling hook, but its grid geometry is fixed
  // (VERTICAL_SLOT_MINUTES per VERTICAL_SLOT_HEIGHT px, starting at the axis
  // top), so minutes convert straight to pixel stops.
  const dayCalendarStyles = useMemo(() => {
    if (!useVerticalDayView) return undefined;

    const openRanges = getOpeningRangesForDate(currentDate, calendarHours, exceptionIndex);
    // Mirror the axis DayCalendar derives from the same hours: the union of the
    // day's ranges snapped out to slot boundaries, or the whole day when the
    // full-day toggle is on (or the workshop is closed and there is no union).
    const axisStart = showExtendedHours || openRanges.length === 0
      ? 0
      : Math.floor((openRanges[0]?.startMinutes ?? 0) / VERTICAL_SLOT_MINUTES) * VERTICAL_SLOT_MINUTES;
    const axisEnd = showExtendedHours || openRanges.length === 0
      ? MINUTES_PER_DAY
      : Math.ceil((openRanges[openRanges.length - 1]?.endMinutes ?? MINUTES_PER_DAY) / VERTICAL_SLOT_MINUTES) * VERTICAL_SLOT_MINUTES;

    const closedRanges = getClosedRangesWithin(
      { startMinutes: axisStart, endMinutes: axisEnd },
      openRanges,
    );
    if (closedRanges.length === 0) return undefined;

    const closedColor = themeMode === 'dark'
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.04);
    const toPx = (minutes: number) =>
      ((minutes - axisStart) / VERTICAL_SLOT_MINUTES) * VERTICAL_SLOT_HEIGHT;

    // One hard-edged band per closed range; `transparent` everywhere else so
    // the slot borders and the column background show through unchanged.
    const stops = closedRanges.flatMap((range) => {
      const top = toPx(range.startMinutes);
      const bottom = toPx(range.endMinutes);
      return [
        `transparent ${String(top)}px`,
        `${closedColor} ${String(top)}px`,
        `${closedColor} ${String(bottom)}px`,
        `transparent ${String(bottom)}px`,
      ];
    });

    return {
      mechanicColumn: {
        backgroundImage: `linear-gradient(to bottom, ${stops.join(', ')})`,
        backgroundRepeat: 'no-repeat',
      },
    };
  }, [useVerticalDayView, currentDate, calendarHours, exceptionIndex, showExtendedHours, themeMode, theme]);

  const dayCalendarMechanics = useMemo<DayCalendarMechanic[]>(() => {
    if (!useVerticalDayView) return [];
    const cols: DayCalendarMechanic[] = [
      { id: UTILIZATION_ROW_ID, name: t('utilization.title') },
      { id: UNASSIGNED_COLUMN_ID, name: t('unassignedColumn'), color: calendarColors.unassigned },
    ];
    for (const m of filteredMechanics) {
      cols.push({ id: m.id, name: `${m.firstName} ${m.lastName}` });
    }
    return cols;
  }, [useVerticalDayView, filteredMechanics, t]);

  // Map filtered service items → ScheduleEvent[] for DayCalendar. Each event
  // carries the original CalendarServiceItem in `data` so renderEvent / click
  // handlers can recover it without a lookup.
  const dayCalendarEvents = useMemo<ScheduleEvent[]>(() => {
    if (!useVerticalDayView) return [];
    return [
      ...absenceBlocks.map((block) => ({
        id: block.id,
        mechanicId: block.mechanicId,
        startTime: block.scheduledStart,
        endTime: block.scheduledEnd,
        title: t(block.labelKey),
        data: { absenceLabelKey: block.labelKey } satisfies AbsenceEventData,
      })),
      ...dateOnlyGroups.map((group) => ({
        id: group.id,
        mechanicId: group.mechanicId ?? UNASSIGNED_COLUMN_ID,
        startTime: group.scheduledStart,
        endTime: group.scheduledEnd,
        title: t('dateOnly.listTitle'),
        data: { dateOnlyGroup: group } satisfies DateOnlyEventData,
      })),
      ...[...unassignedItems, ...mechanicItems].map((item) => ({
        id: item.id,
        mechanicId: item.assignedMechanicId ?? UNASSIGNED_COLUMN_ID,
        startTime: item.scheduledStart,
        endTime: item.scheduledEnd,
        title: item.serviceName,
        data: item,
      })),
    ];
  }, [useVerticalDayView, unassignedItems, mechanicItems, dateOnlyGroups, absenceBlocks, t]);

  const handleVerticalSlotClick = useCallback(
    (mechanicId: string, datetime: Date) => {
      if (mechanicId === UTILIZATION_ROW_ID) return;
      handleEmptyCellClick(
        datetime,
        mechanicId === UNASSIGNED_COLUMN_ID ? null : mechanicId
      );
    },
    [handleEmptyCellClick]
  );

  const handleVerticalEventClick = useCallback(
    (_eventId: string, event: ScheduleEvent) => {
      // A day list opens its own menu; the event click must not open a work order.
      if (isAbsenceEventData(event.data) || isDateOnlyEventData(event.data)) return;
      const item = event.data as CalendarServiceItem | undefined;
      if (item) handleItemClick(item);
    },
    [handleItemClick]
  );

  // Drag & drop in the vertical view. Unlike a timeline row change, which
  // snaps to the new mechanic's shift start, a column drop carries the time
  // the user dropped it at — the vertical axis makes that choice explicit.
  const handleVerticalEventDrop = useCallback(
    (drop: DayCalendarEventDrop) => {
      if (!isDraggableDayEvent(drop.event)) return;
      const item = drop.event.data as CalendarServiceItem;
      if (drop.mechanicId !== drop.previousMechanicId && onAssign) {
        const mechanicId = drop.mechanicId === UNASSIGNED_COLUMN_ID ? null : drop.mechanicId;
        onAssign(item.sourceId, item.bookingId, mechanicId, drop.startTime);
      } else if (onReschedule) {
        onReschedule(item.sourceId, item.bookingId, drop.startTime);
      }
    },
    [onAssign, onReschedule]
  );

  const canDropDayEvent = useCallback(
    (_event: ScheduleEvent, mechanicId: string) => mechanicId !== UTILIZATION_ROW_ID,
    []
  );

  // Subtle zebra stripe for mechanic rows (even/odd). Keeps rows separable
  // without horizontal divider lines, which clutter a dense timeline.
  const rowStripeBg = useCallback(
    (index: number) =>
      index % 2 === 1
        ? alpha(
            themeMode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
            themeMode === 'dark' ? 0.045 : 0.04,
          )
        : 'transparent',
    [themeMode, theme.palette.common.white, theme.palette.common.black],
  );

  const unassignedRowBg = alpha(
    theme.palette.warning.main,
    themeMode === 'dark' ? 0.08 : 0.06,
  );

  // Memoize sidebar configuration to prevent recreation on every render
  // This stabilizes the sidebar prop and prevents scroll sync issues
  const sidebarConfig = useMemo(() => {
    if (isMobile) return undefined;
    const compact = density === 'compact';
    const avatarSize = densityMetrics.avatarSize;
    const rowPadX = compact ? 1 : 1.5;

    return {
      width: densityMetrics.sidebarWidth,
      content: (
        <>
          <Box sx={{ height: UTILIZATION_ROW_HEIGHT, display: 'flex', alignItems: 'center', px: rowPadX, bgcolor: 'action.hover' }}>
            <Typography variant="caption" fontWeight={700}>{t('utilization.title')}</Typography>
          </Box>
          <Box
            sx={{
              height: getRowHeight('unassigned'),
              display: 'flex',
              alignItems: 'center',
              gap: compact ? 0.75 : 1,
              px: rowPadX,
              boxSizing: 'border-box',
              bgcolor: unassignedRowBg,
            }}
          >
            <Avatar sx={{ width: avatarSize, height: avatarSize, bgcolor: 'warning.main', fontSize: compact ? 12 : 14 }}>?</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" fontWeight={600} noWrap lineHeight={1.2}>
                {t('unassignedRow')}
              </Typography>
            </Box>
          </Box>
          {filteredMechanics.map((mechanic, index) => (
            <Box
              key={mechanic.id}
              sx={{
                height: getRowHeight(mechanic.id),
                display: 'flex',
                alignItems: 'center',
                gap: compact ? 0.75 : 1,
                px: rowPadX,
                boxSizing: 'border-box',
                bgcolor: rowStripeBg(index),
              }}
            >
              <UserAvatar
                firstName={mechanic.firstName}
                lastName={mechanic.lastName}
                size={avatarSize}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={600} noWrap lineHeight={1.2}>
                  {compact
                    ? `${mechanic.firstName} ${mechanic.lastName.charAt(0)}.`
                    : mechanic.firstName}
                </Typography>
                {!compact && (
                  <Typography variant="caption" color="text.secondary" noWrap lineHeight={1.2} display="block">
                    {mechanic.lastName}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </>
      ),
    };
  }, [isMobile, filteredMechanics, t, getRowHeight, rowStripeBg, unassignedRowBg, density, densityMetrics]);

  return (
    <Box
      className="os-timeline-root"
      sx={
        fill
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
    >
      <CalendarToolbar
        currentDate={currentDate}
        view={view}
        dayLayout={dayLayout}
        showExtendedHours={showExtendedHours}
        compressClosedHours={compressClosedHours}
        mechanics={pickerMechanics}
        serviceTypes={serviceTypes}
        selectedMechanicIds={selectedMechanicIds}
        selectedStatuses={selectedStatuses}
        selectedServiceTypes={selectedServiceTypes}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onToday={handleToday}
        onDateChange={setCurrentDate}
        onViewChange={handleViewChange}
        onDayLayoutChange={handleDayLayoutChange}
        onShowExtendedHoursChange={setShowExtendedHours}
        onCompressClosedHoursChange={setCompressClosedHours}
        onMechanicChange={setSelectedMechanicIds}
        onStatusChange={setSelectedStatuses}
        onServiceTypeChange={setSelectedServiceTypes}
        onNewWorkOrder={handleNewWorkOrder}
        density={density}
        onDensityChange={setStoredDensity}
        mechanicGroups={mechanicGroups}
        onSaveMechanicGroup={handleSaveMechanicGroup}
        onApplyMechanicGroup={handleApplyMechanicGroup}
        onDeleteMechanicGroup={deleteMechanicGroup}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <CalendarStatusLegend />
        <MechanicFilter
          hideAbsent={hideAbsentMechanics}
          onHideAbsentChange={setHideAbsentMechanics}
        />
      </Box>

      {capacityGap !== null && (
        <CapacityGapAlert gap={capacityGap} onShowAllMechanics={handleShowAllMechanics} />
      )}

      <UnplacedWorkBucket
        items={unplacedWork}
        onSelect={openWorkOrder}
      />

      <Paper
        elevation={0}
        className="workshop-timeline-calendar"
        data-testid="workshop-timeline-calendar"
        data-density={density}
        data-event-height={String(timelineEventHeight(density))}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          touchAction: isMobile && view !== 'month' ? 'pan-y' : 'auto',
          position: 'relative',
          ...(fill && { flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
        }}
        aria-busy={loading}
        {...(isMobile && view !== 'month' ? swipeHandlers : {})}
      >
        {loading && (
          <LinearProgress
            aria-label={tc('loading')}
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}
          />
        )}
        {useVerticalDayView ? (
          <Box
            ref={dayCalendarContainerRef}
            sx={fill ? { flex: 1, minHeight: 0 } : undefined}
          >
          <DayCalendar
            date={currentDate}
            mechanics={dayCalendarMechanics}
            events={dayCalendarEvents}
            availability={dayCalendarAvailability}
            slotMinutes={VERTICAL_SLOT_MINUTES}
            slotHeight={VERTICAL_SLOT_HEIGHT}
            minColumnWidth={dayColumnMinWidth(filteredMechanics.length)}
            height={calendarHeight}
            showCurrentTime
            classNames={{ currentTimeLine: 'workshop-calendar-current-time' }}
            styles={{ ...dayCalendarStyles, header: { height: 72, flexShrink: 0 } }}
            locale={calendarLocaleFor(currentLanguage())}
            theme={themeMode}
            renderMechanicHeader={(mechanic) => mechanic.id === UTILIZATION_ROW_ID ? (
              <Box>
                <Typography variant="caption" fontWeight={700}>{t('utilization.title')}</Typography>
                {dailyUtilization[0] && <UtilizationSummary day={dailyUtilization[0]} unavailable={!utilizationReady} />}
              </Box>
            ) : mechanic.name}
            onSlotClick={handleVerticalSlotClick}
            onEventClick={handleVerticalEventClick}
            // Touch keeps scroll and day swipe; dragging is a pointer gesture.
            draggableEvents={isMobile ? false : isDraggableDayEvent}
            dragSnapMinutes={VERTICAL_DRAG_SNAP_MINUTES}
            canDropEvent={canDropDayEvent}
            onEventDrop={handleVerticalEventDrop}
            renderEvent={({ event, top, height, left, width, draggable, isDragging, isDragPreview }) => {
              const absence = isAbsenceEventData(event.data) ? event.data : null;
              const dateOnly = isDateOnlyEventData(event.data) ? event.data.dateOnlyGroup : null;
              const item = absence || dateOnly ? undefined : event.data as CalendarServiceItem | undefined;
              if (!absence && !dateOnly && !item) return null;
              return (
                <Box
                  key={event.id}
                  sx={{
                    position: 'absolute',
                    top,
                    height,
                    left: `${String(left)}%`,
                    width: `${String(width)}%`,
                    boxSizing: 'border-box',
                    padding: '1px 2px',
                    ...(draggable && { cursor: isDragPreview ? 'grabbing' : 'grab', userSelect: 'none' }),
                    ...(isDragging && { opacity: 0.4 }),
                    ...(isDragPreview && { zIndex: 4, '& > *': { boxShadow: 6 } }),
                  }}
                >
                  {absence ? (
                    <AbsenceCalendarItem labelKey={absence.absenceLabelKey} />
                  ) : dateOnly ? (
                    <DateOnlyDayItem group={dateOnly} onItemClick={handleItemClick} />
                  ) : item ? (
                    <ServiceCalendarItem
                      item={item}
                      onClick={() => handleItemClick(item)}
                    />
                  ) : null}
                </Box>
              );
            }}
          />
          </Box>
        ) : (
        <TimelineCalendar
          startDate={startDate}
          endDate={endDate}
          height={calendarHeight}
          showCurrentTime={true}
          locale={calendarLocaleFor(currentLanguage())}
          theme={themeMode}
          onViewportChange={handleViewportChange}
          // Compression derives opening hours from its own availability, which has
          // to stay the weekday pattern — the all-closed baseline above would tell
          // it the workshop is never open.
          compressClosedHours={
            compressClosedHours ? { availability: { weekly: rangeAvailabilityWeekly } } : false
          }
          availability={{
            ...timelineAvailability,
            unavailableStyle: {
              backgroundColor:
                themeMode === 'dark'
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.common.black, 0.04),
            },
          }}
          sidebar={sidebarConfig}
        >
          <TimelineRowGroup>
            <CalendarClickOverlay
              calendarHours={calendarHours}
              onEmptyClick={handleEmptyCellClick}
            />
            <TimelineRow id={UTILIZATION_ROW_ID} label={t('utilization.title')}
              rowCount={1} height={UTILIZATION_ROW_HEIGHT} collapsible={false}>
              {dailyUtilization.map((day) => {
                // No capacity → no ratio to show; leave the day empty.
                if (utilizationReady && day.percent === null) return null;
                const date = new Date(`${day.date}T00:00:00`);
                return (
                  <TimelineItem key={day.date}
                    startTime={Math.max(date.getTime(), startDate.getTime())}
                    endTime={Math.min(addDays(date, 1).getTime(), endDate.getTime())}
                    row={0} draggable={false} style={{ height: UTILIZATION_ROW_HEIGHT - 6 }}>
                    <UtilizationSummary day={day} unavailable={!utilizationReady} />
                  </TimelineItem>
                );
              })}
            </TimelineRow>
            {/* Unassigned row — always visible as drop target for cross-row drag */}
            <TimelineRow
              key={`unassigned#${rowHeightsSignature}`}
              id="unassigned"
              label={t('unassignedRow')}
              rowCount={1}
              height={getRowHeight('unassigned')}
              collapsible={false}
              style={{
                backgroundColor: unassignedRowBg,
              }}
            >
              {/* Only mount items that intersect the height viewport. The library
                  packs subRows from *all* TimelineItem children — if we keep
                  off-screen morning overlaps mounted while the row is sized for
                  a quiet afternoon, bars are stacked into too-short slots and
                  overflow past the last row, which forces a spurious v-scrollbar
                  (content height measured from absolute children bottoms). */}
              {unassignedDateOnlyGroups
                .filter((group) =>
                  itemIntersectsRange(group, heightViewportMs.start, heightViewportMs.end),
                )
                .map((group) => (
                  <TimelineItem
                    key={group.id}
                    startTime={group.scheduledStart}
                    endTime={group.scheduledEnd}
                    row={0}
                    style={eventItemStyle(getRowHeight('unassigned'), density)}
                    draggable={false}
                  >
                    <DateOnlyDayItem group={group} onItemClick={handleItemClick} />
                  </TimelineItem>
                ))}
              {unassignedItems
                .filter((item) =>
                  itemIntersectsRange(item, heightViewportMs.start, heightViewportMs.end),
                )
                .map((item) => (
                <TimelineItem
                  key={`unassigned-${item.id}`}
                  startTime={item.scheduledStart}
                  endTime={item.scheduledEnd}
                  row={0}
                  style={eventItemStyle(getRowHeight('unassigned'), density)}
                  draggable={isServiceItemDraggable(item)}
                  allowRowChange={true}
                  onDragEnd={(newTime, oldTime, _newRow, _oldRow, newGroupId, oldGroupId) =>
                    handleDragEnd(item.sourceId, item.bookingId, newTime, oldTime, newGroupId, oldGroupId)
                  }
                >
                  <ServiceCalendarItem
                    item={item}
                    onClick={() => handleItemClick(item)}
                  />
                </TimelineItem>
              ))}
            </TimelineRow>

            {/* Mechanic rows — service items assigned to this mechanic */}
            {filteredMechanics.map((mechanic, index) => (
              <TimelineRow
                key={`${mechanic.id}#${rowHeightsSignature}`}
                id={mechanic.id}
                label={isMobile ? `${mechanic.firstName} ${mechanic.lastName.charAt(0)}.` : `${mechanic.firstName} ${mechanic.lastName}`}
                rowCount={1}
                height={getRowHeight(mechanic.id)}
                collapsible={false}
                style={{ backgroundColor: rowStripeBg(index) }}
                bottomBars={[
                  ...Array.from(clockTimeMap.entries())
                    .filter(([key]) => key.startsWith(`${mechanic.id}|`))
                    .flatMap(([, ct]) => {
                      if (!ct.clockIn) return [];
                      return [{
                        startTime: ct.clockIn,
                        endTime: ct.clockOut ?? new Date().toISOString(),
                        color: theme.palette.text.secondary,
                      }];
                    }),
                  ...shifts
                    .filter((s) => s.userId === mechanic.id)
                    .map((shift) => ({
                      startTime: `${shift.date}T${shift.startTime}`,
                      endTime: `${shift.date}T${shift.endTime}`,
                      color: shift.color,
                      layer: 1,
                    })),
                ]}
              >
                {/* Absence bars — a sick leave or vacation day has no shift
                    (#2261), so this is the row's only content on such a day. */}
                {absenceBlocks
                  .filter(
                    (block) =>
                      block.mechanicId === mechanic.id &&
                      itemIntersectsRange(block, heightViewportMs.start, heightViewportMs.end),
                  )
                  .map((block) => (
                    <TimelineItem
                      key={block.id}
                      startTime={block.scheduledStart}
                      endTime={block.scheduledEnd}
                      row={0}
                      style={eventItemStyle(getRowHeight(mechanic.id), density)}
                      draggable={false}
                    >
                      <AbsenceCalendarItem labelKey={block.labelKey} />
                    </TimelineItem>
                  ))}

                {/* Date-only history: one day list, never draggable (#2760). */}
                {dateOnlyGroups
                  .filter(
                    (group) =>
                      group.mechanicId === mechanic.id &&
                      itemIntersectsRange(group, heightViewportMs.start, heightViewportMs.end),
                  )
                  .map((group) => (
                    <TimelineItem
                      key={group.id}
                      startTime={group.scheduledStart}
                      endTime={group.scheduledEnd}
                      row={0}
                      style={eventItemStyle(getRowHeight(mechanic.id), density)}
                      draggable={false}
                    >
                      <DateOnlyDayItem group={group} onItemClick={handleItemClick} />
                    </TimelineItem>
                  ))}

                {/* Service items assigned to this mechanic — viewport-filtered
                    so library subRow packing matches viewport-based row heights. */}
                {mechanicItems
                  .filter(
                    (item) =>
                      item.assignedMechanicId === mechanic.id &&
                      itemIntersectsRange(item, heightViewportMs.start, heightViewportMs.end),
                  )
                  .map((item) => (
                    <TimelineItem
                      key={`${mechanic.id}-${item.id}`}
                      startTime={item.scheduledStart}
                      endTime={item.scheduledEnd}
                      row={0}
                      style={eventItemStyle(getRowHeight(mechanic.id), density)}
                      draggable={isServiceItemDraggable(item)}
                      allowRowChange={true}
                      onDragEnd={(newTime, oldTime, _newRow, _oldRow, newGroupId, oldGroupId) =>
                        handleDragEnd(item.sourceId, item.bookingId, newTime, oldTime, newGroupId, oldGroupId)
                      }
                    >
                      <ServiceCalendarItem
                        item={item}
                        onClick={() => handleItemClick(item)}
                      />
                    </TimelineItem>
                  ))}

                {/* Events/Pinpoints */}
                <TimelinePinpointGroup row={0} clusterDistance={20}>
                  {events
                    .filter((e) => e.mechanicId === mechanic.id)
                    .map((event) => (
                      <TimelinePinpoint
                        key={event.id}
                        time={event.time}
                        color={eventColorPaths[event.type](theme)}
                        size={20}
                        alignment="top"
                      >
                        {eventIcons[event.type]}
                      </TimelinePinpoint>
                    ))}
                </TimelinePinpointGroup>
              </TimelineRow>
            ))}
          </TimelineRowGroup>
        </TimelineCalendar>
        )}
      </Paper>

      <WorkOrderDetailDialog
        workOrderId={selectedWorkOrderId}
        open={modalOpen}
        onClose={handleCloseModal}
      />

      <WorkOrderCreateModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        mechanics={mechanics}
        initialDate={createInitialDate}
        initialMechanicId={createInitialMechanicId}
      />

      {/* Empty-cell flow (#2653): mounted only while in use, so the quote list
          is fetched on demand and nothing renders behind the calendar. */}
      <EmptySlotChoiceDialog
        open={emptySlot !== null && !quotePickerOpen && acceptingQuoteId === null}
        date={emptySlot?.date ?? null}
        mechanicName={emptySlotMechanicName}
        onNewWorkOrder={handleSlotNewWorkOrder}
        onBookQuote={handleSlotBookQuote}
        onClose={() => { setEmptySlot(null); }}
      />
      {quotePickerOpen && (
        <OpenQuotePickerDialog
          open
          onSelect={handleQuotePicked}
          onClose={() => { setQuotePickerOpen(false); }}
        />
      )}
      {acceptingQuoteId !== null && (
        <QuoteAcceptFlow
          quoteId={acceptingQuoteId}
          initialScheduledAt={emptySlot?.date}
          initialMechanicId={emptySlot?.mechanicId ?? undefined}
          onClose={handleQuoteFlowClosed}
          onAccepted={handleQuoteAccepted}
        />
      )}
    </Box>
  );
}
